/**
 * Shared login/register form internals for the four role-specific auth pages.
 * Inline field validation, an aria-live error summary, sticky submit footer,
 * loading + disabled submit states. No optimistic app entry: navigation only
 * happens after signIn/signUp has fully resolved.
 */
import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Banner, Button, PasswordField, StatusCard, TextField } from '@/components/ui'
import { PATHS } from '@/app/router/paths'
import { portalHomeForRole, type AuthStateLocation } from '@/app/router/RequireAuth'
import { isCloudConfigured } from '@/lib/supabase/client'
import { isAppError, toAppError } from '@/lib/supabase/errors'
import type { AppError } from '@/types'
import { useAuth } from '../useAuth'
import type { PortalRole } from '../authContext'
import {
  PASSWORD_POLICY_HINT,
  validateDisplayName,
  validateEmail,
  validatePassword,
  validatePasswordConfirm,
} from '../validation'
import { NotConfiguredBanner } from './AuthLayout'

function safeReturnPath(state: unknown, role: PortalRole): string | null {
  const from = (state as AuthStateLocation | null)?.from
  if (typeof from !== 'string' || !from.startsWith('/')) return null
  // Never bounce a user into the other role's portal via a stale return path.
  if (role === 'caregiver' && !from.startsWith('/caregiver')) return null
  if (role === 'client' && !from.startsWith('/app')) return null
  return from
}

export function roleLoginPath(role: PortalRole): string {
  return role === 'caregiver' ? PATHS.auth.caregiverLogin : PATHS.auth.clientLogin
}

export function roleRegisterPath(role: PortalRole): string {
  return role === 'caregiver' ? PATHS.auth.caregiverRegister : PATHS.auth.clientRegister
}

export function LoginForm({ role }: { role: PortalRole }) {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const configured = isCloudConfigured()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({})
  const [formError, setFormError] = useState<AppError | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting || !configured) return

    const errors: { email?: string; password?: string } = {}
    const emailError = validateEmail(email)
    if (emailError) errors.email = emailError
    if (password.length === 0) errors.password = 'Vul je wachtwoord in.'
    setFieldErrors(errors)
    if (errors.email || errors.password) return

    setSubmitting(true)
    setFormError(null)
    try {
      await signIn({ email, password, role })
      const target = safeReturnPath(location.state, role) ?? portalHomeForRole(role)
      navigate(target, { replace: true })
    } catch (error) {
      setFormError(isAppError(error) ? error : toAppError(error, 'auth'))
      setSubmitting(false)
    }
  }

  return (
    <form className="auth-card" onSubmit={onSubmit} noValidate>
      {!configured && <NotConfiguredBanner />}
      {formError && (
        <Banner tone="error">
          {formError.userMessage}
        </Banner>
      )}

      <TextField
        label="E-mailadres"
        type="email"
        value={email}
        onChange={setEmail}
        error={fieldErrors.email}
        required
        autoComplete="email"
        placeholder="naam@voorbeeld.nl"
        disabled={!configured || submitting}
        onBlur={() => setFieldErrors((prev) => ({ ...prev, email: validateEmail(email) ?? undefined }))}
      />
      <PasswordField
        label="Wachtwoord"
        value={password}
        onChange={setPassword}
        error={fieldErrors.password}
        required
        autoComplete="current-password"
        disabled={!configured || submitting}
      />

      <div className="auth-form-footer">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={!configured || submitting}
        >
          Inloggen
        </Button>
      </div>

      <div className="auth-links auth-links--dark">
        <Link to={PATHS.auth.forgotPassword}>Wachtwoord vergeten?</Link>
        <Link to={roleRegisterPath(role)}>Nog geen account? Registreren</Link>
      </div>
    </form>
  )
}

export function RegisterForm({ role }: { role: PortalRole }) {
  const { signUp } = useAuth()
  const navigate = useNavigate()
  const configured = isCloudConfigured()

  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [fieldErrors, setFieldErrors] = useState<{
    displayName?: string
    email?: string
    password?: string
    confirm?: string
  }>({})
  const [formError, setFormError] = useState<AppError | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [needsEmailConfirmation, setNeedsEmailConfirmation] = useState(false)

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault()
    if (submitting || !configured) return

    const errors: typeof fieldErrors = {}
    const nameError = validateDisplayName(displayName)
    if (nameError) errors.displayName = nameError
    const emailError = validateEmail(email)
    if (emailError) errors.email = emailError
    const passwordError = validatePassword(password)
    if (passwordError) errors.password = passwordError
    const confirmError = validatePasswordConfirm(password, confirm)
    if (confirmError) errors.confirm = confirmError
    setFieldErrors(errors)
    if (Object.keys(errors).length > 0) return

    setSubmitting(true)
    setFormError(null)
    try {
      const result = await signUp({ email, password, displayName, role })
      if (result.needsEmailConfirmation) {
        setNeedsEmailConfirmation(true)
        setSubmitting(false)
      } else {
        navigate(portalHomeForRole(role), { replace: true })
      }
    } catch (error) {
      setFormError(isAppError(error) ? error : toAppError(error, 'auth'))
      setSubmitting(false)
    }
  }

  if (needsEmailConfirmation) {
    return (
      <div className="auth-card">
        <StatusCard
          status="info"
          title="Bevestig je e-mailadres"
          body={`We hebben een e-mail gestuurd naar ${email.trim()}. Open de link in die e-mail om je account te activeren. Daarna kun je inloggen.`}
          action={
            <Button variant="primary" fullWidth onClick={() => navigate(roleLoginPath(role))}>
              Naar inloggen
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <form className="auth-card" onSubmit={onSubmit} noValidate>
      {!configured && <NotConfiguredBanner />}
      {formError && (
        <Banner tone="error">
          {formError.userMessage}
        </Banner>
      )}

      <TextField
        label="Naam"
        value={displayName}
        onChange={setDisplayName}
        error={fieldErrors.displayName}
        required
        autoComplete="name"
        placeholder="Hoe mogen we je noemen?"
        disabled={!configured || submitting}
        onBlur={() =>
          setFieldErrors((prev) => ({ ...prev, displayName: validateDisplayName(displayName) ?? undefined }))
        }
      />
      <TextField
        label="E-mailadres"
        type="email"
        value={email}
        onChange={setEmail}
        error={fieldErrors.email}
        required
        autoComplete="email"
        placeholder="naam@voorbeeld.nl"
        disabled={!configured || submitting}
        onBlur={() => setFieldErrors((prev) => ({ ...prev, email: validateEmail(email) ?? undefined }))}
      />
      <PasswordField
        label="Wachtwoord"
        value={password}
        onChange={setPassword}
        error={fieldErrors.password}
        hint={PASSWORD_POLICY_HINT}
        required
        autoComplete="new-password"
        disabled={!configured || submitting}
        onBlur={() =>
          setFieldErrors((prev) => ({ ...prev, password: validatePassword(password) ?? undefined }))
        }
      />
      <PasswordField
        label="Herhaal wachtwoord"
        value={confirm}
        onChange={setConfirm}
        error={fieldErrors.confirm}
        required
        autoComplete="new-password"
        disabled={!configured || submitting}
        onBlur={() =>
          setFieldErrors((prev) => ({
            ...prev,
            confirm: validatePasswordConfirm(password, confirm) ?? undefined,
          }))
        }
      />

      <div className="auth-form-footer">
        <Button
          type="submit"
          variant="primary"
          size="lg"
          fullWidth
          loading={submitting}
          disabled={!configured || submitting}
        >
          Account aanmaken
        </Button>
      </div>

      <div className="auth-links auth-links--dark">
        <Link to={roleLoginPath(role)}>Al een account? Inloggen</Link>
      </div>
    </form>
  )
}
